import {
    inject,
    Injectable
} from '@angular/core';

import {
    TranslateService
} from '@ngx-translate/core';

import {
    GenericListDialogService,
    WaitDialogService,
    WaitDialogModel,
} from 'src/app/dialogs';

import {
    readJsonOrThrow
} from 'src/app/shared/net/fetch-response';

import {
    DigitalPaymentHandler,
    DigitalPaymentHandlerOptions,
    DigitalPaymentHandlerResult
} from 'src/app/shared/model/digital-payments.model';

import {
    SalesPayments
} from 'tilby-models';

import {
    ZPay
} from './z-pay.model';

import {
    asyncTimeout,
    decryptCredentials,
    MathUtils
} from 'src/app/shared/utils';

import {
    Subject
} from 'rxjs';

import {
    SaleUtilsService
} from 'src/app/features/cashregister';

type PaymentConfig = {
    applicationKey: string;
    merchantKey: string;
    terminalId: string;
    stagingMode: boolean;
    useTip: boolean;
    paymentMethod: ZPay.PaymentMethod;
    alternativePayment?: ZPay.AlternativePayment
}

type SelectableMethods = 'auto' | 'ticket' | 'satispay';

const availableMethods = new Set<SelectableMethods>([
    'auto',
    'ticket',
    'satispay'
]);

@Injectable({
    providedIn: 'root'
})
export class ZPayService implements DigitalPaymentHandler {
    private readonly genericListDialogService = inject(GenericListDialogService);
    private readonly saleUtilsService = inject(SaleUtilsService);
    private readonly translateService = inject(TranslateService);
    private readonly waitDialogService = inject(WaitDialogService);

    public getPaymentReceipt(paymentMethod: SalesPayments): string {
        //TODO: implement
        return '';
    }

    private getBaseUrl(stagingMode: boolean) {
        return stagingMode
            ? 'https://api.staging.zpay.it'
            : 'https://api.zpay.it';
    }

    private getAuthorization(merchantKey: string, applicationKey?: string) {
        const applicationKeyToUse = applicationKey || decryptCredentials('KZ1ToFFczNHSVJnc4V0YjpVb1RFeC5kZHNGS0RUWF12UxZ0RXBjVxFzTLp3N0s2c4NHVVJVTBZVahRjM');

        return `Basic ${Buffer.from(`${applicationKeyToUse}:${merchantKey}`).toString('base64')}`;
    }

    private async getStores(authorization: string, stagingMode: boolean): Promise<ZPay.GetSalesChannelsResponse> {
        const baseUrl = this.getBaseUrl(stagingMode);

        const res = await fetch(`${baseUrl}/api/sales_channels`, {
            method: 'GET',
            headers: {
                'Authorization': authorization
            }
        });

        return readJsonOrThrow(res);
    }

    private async getTransactionStatus(transactionID: string, authorization: string, stagingMode: boolean): Promise<ZPay.PaymentChargeResponse> {
        const baseUrl = this.getBaseUrl(stagingMode);

        const res = await fetch(`${baseUrl}/api/payment_charges/${transactionID}`, {
            method: 'GET',
            headers: {
                'Authorization': authorization
            }
        });

        return readJsonOrThrow(res);
    }

    private async sendChargeRequest(transactionRequest: ZPay.PaymentChargeRequest, authorization: string, stagingMode: boolean): Promise<ZPay.PaymentChargeResponse> {
        const baseUrl = this.getBaseUrl(stagingMode);

        const res = await fetch(`${baseUrl}/api/payment_charges`, {
            method: 'POST',
            body: JSON.stringify(transactionRequest),
            headers: {
                'Authorization': authorization,
                'Content-Type': 'application/json',
            },
        });

        return readJsonOrThrow(res);
    }

    private getStatusType(status: ZPay.PaymentChargeResponse['status']): ('success' | 'failed' | 'pending' | 'unknown') {
        switch (status) {
            case 'SUCCEEDED':
            case 'REFUNDED':
            case 'PARTIALLY_REFUNDED':
                return 'success';
            case 'CANCELED':
            case 'FAILED':
            case 'EXPIRED':
            case 'POS_CANCELLED':
                return 'failed';
            case 'PROCESSING':
            case 'WAITING_FOR_PAYMENT':
            case 'REQUIRES_CAPTURE':
            case 'PARTIALLY_CAPTURED':
                return 'pending';
            default:
                return 'unknown';
        }
    }


    private async paymentRequestFlow(amount: number, statusSubject: WaitDialogModel.StatusSubject, config: PaymentConfig) {
        const {
            applicationKey,
            merchantKey,
            terminalId,
            useTip,
            stagingMode,
            paymentMethod,
            alternativePayment
        } = config;

        const transactionRequest: ZPay.PaymentChargeRequest = {
            amount: amount,
            paymentMethod: paymentMethod,
            alternativePayment: alternativePayment,
            paymentMethodInfo: {
                terminalId: terminalId,
                tip: (useTip && paymentMethod === 'POS' && !alternativePayment) ? true : undefined
            }
        };

        const authorization = this.getAuthorization(merchantKey, applicationKey);

        let paymentReqData = await this.sendChargeRequest(transactionRequest, authorization, stagingMode);
        const transactionID = paymentReqData.id;

        let paymentStatusType = this.getStatusType(paymentReqData.status);

        while (paymentStatusType === 'pending') {
            // Update the wait dialog with the updated status
            statusSubject.next({
                message: this.translateService.instant(
                    paymentReqData.status === 'WAITING_FOR_PAYMENT'
                        ? 'DIGITAL_PAYMENTS.INGENICO_17.COMPLETE_ON_POS'
                        : `DIGITAL_PAYMENTS.ZPAY.${paymentReqData.status}`
                )
            });

            // Wait a bit before asking for the status update
            await asyncTimeout(1500);

            // Ask for the status update
            paymentReqData = await this.getTransactionStatus(transactionID, authorization, stagingMode);
            paymentStatusType = this.getStatusType(paymentReqData.status);
        }

        if (paymentStatusType !== 'success') {
            const errorMessage = paymentReqData.error?.message;

            throw paymentStatusType === 'failed'
                ? `${this.translateService.instant(`DIGITAL_PAYMENTS.ZPAY.${paymentReqData.status}`)} ${errorMessage ? `(${errorMessage})` : ''}`
                : `${paymentReqData.status}${errorMessage ? `: (${errorMessage})` : ''}`;
        }

        return paymentReqData;
    }

    public async payment(amount: number, options: DigitalPaymentHandlerOptions): Promise<DigitalPaymentHandlerResult[]> {
        const paymentMethod = options.paymentMethod;

        const {
            applicationKey, // Application Key can be overridden in the payment method configuration (e.g. for testing), otherwise it will use the default one
            merchantKey,
            terminalId,
            stagingMode,
        } = (paymentMethod.configuration || {});

        if (!merchantKey) {
            throw 'DIGITAL_PAYMENTS.Z_PAY.MISSING_MERCHANT_KEY';
        }

        const useTip = !!options.paymentMethod.configuration?.useTip;

        if (useTip) {
            //Try to obtain a tip sale item to check if tips are properly configured
            await this.saleUtilsService.getTipSaleItem(amount);
        }

        let targetTerminalId = terminalId;

        if (!targetTerminalId) {
            const stores = await this.getStores(this.getAuthorization(merchantKey, applicationKey), stagingMode);

            const availableTerminals = stores
                .flatMap(store => (store.terminalIds || [])
                    .map((terminalId) => ({
                        terminalId: terminalId,
                        storeName: store.name || store.label,
                    }))
                );

            switch (availableTerminals.length) {
                case 0:
                    throw 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.NO_POS_AVAILABLE';
                case 1:
                    targetTerminalId = availableTerminals[0].terminalId;
                    break;
                default:
                    const selectedTerminal = await this.genericListDialogService.openDialog({
                        data: {
                            title: { label: 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.SELECT_POS' },
                            list: availableTerminals.map(terminal => ({
                                name: `${terminal.storeName} (${terminal.terminalId})`,
                                value: terminal.terminalId,
                                isSelected: false
                            }))
                        }
                    });

                    if (!selectedTerminal) {
                        throw 'CANCELED';
                    }

                    targetTerminalId = selectedTerminal;
            }
        }

        // Method Selection
        let selectedMethod: SelectableMethods | undefined;

        if (options.sale.payments?.some(payment => payment.payment_method_type_id === 46 && payment.unclaimed)) {
            selectedMethod = 'auto';
        }

        const availableMethodsArr = [...availableMethods.values()].map((type) => ({
            name: `DIGITAL_PAYMENTS.ZPAY_BRIDGE.PAYMENT_TYPE.${type.toUpperCase()}`,
            value: type,
            isSelected: false
        }));

        if (!selectedMethod) {
            selectedMethod = await this.genericListDialogService.openDialog({
                data: {
                    title: { label: 'DIGITAL_PAYMENTS.ZPAY_BRIDGE.SELECT_PAYMENT_TYPE' },
                    list: availableMethodsArr
                }
            });
        }

        if (!selectedMethod) {
            throw 'CANCELED';
        }

        // Payment
        let finalMethod: ZPay.PaymentMethod;
        let alternativePayment: ZPay.AlternativePayment | undefined;

        switch (selectedMethod) {
            case 'satispay':
                finalMethod = 'POS';
                alternativePayment = 'SATISPAY';
                break;
            case 'ticket':
                finalMethod = 'MEAL_VOUCHER';
                break;
            case 'auto':
            default:
                finalMethod = 'POS';
                break;
        }

        const statusSubject: WaitDialogModel.StatusSubject = new Subject();

        const config: PaymentConfig = {
            applicationKey,
            merchantKey,
            terminalId: targetTerminalId,
            useTip,
            stagingMode,
            paymentMethod: finalMethod,
            alternativePayment: alternativePayment
        };

        const paymentPromise = this.paymentRequestFlow(amount, statusSubject, config);

        const response = await this.waitDialogService.openDialog<ZPay.PaymentChargeResponse>({
            data: {
                message: 'DIGITAL_PAYMENTS.INGENICO_17.COMPLETE_ON_POS',
                promise: paymentPromise,
                stateSubject: statusSubject
            }
        });

        const mainResult: DigitalPaymentHandlerResult = {
            amount: response.amount,
            payment_data: JSON.stringify(response),
            unclaimed: false
        };

        if (selectedMethod === 'ticket') {
            mainResult.unclaimed = true;
        }

        const result = [mainResult];

        if (useTip && response.tipAmount) {
            mainResult.amount = MathUtils.round(response.amount - response.tipAmount);

            result.push({
                amount: response.tipAmount,
                is_tip: true,
                unclaimed: false,
            });
        }

        let methodName = availableMethodsArr.find(method => method.value === selectedMethod)?.name;

        if (methodName) {
            methodName = this.translateService.instant(methodName);

            for (const method of result) {
                method.payment_method_name = method.is_tip
                    ? `${methodName} mancia`
                    : methodName;
            }
        }

        return result;
    }
}